import { Stack, Typography } from '@mui/material';

import { Alert } from '@/components/notifications/Alert/Alert';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderWithListV2 } from '@/modules/documents/MultiDocumentUploader/MultiDocumentUploaderWithListV2';
import { DocumentType } from '@/types/schema';

export interface DocumentUploadWithFrameProps {
  /**
   * Whether to hide the header and subheader
   */
  hideHeader?: boolean;
  /**
   * The household ID for document upload
   */
  householdId: string;
  /**
   * Main header text to display
   */
  header: string;
  /**
   * Subheader text to display
   */
  subheader: string;
  /**
   * Whether to allow selecting existing documents
   */
  allowSelectExistingDocuments?: boolean;
  /**
   * Existing document types to exclude from the picker
   */
  excludedExistingDocumentTypes?: DocumentType[];
  /**
   * Error message to display
   */
  errorMessage?: string | null;
  /**
   * Handler to clear the error message
   */
  onClearError?: () => void;
  /**
   * Additional content to render above the uploader (e.g., feature toggles)
   */
  additionalContentAboveUploader?: React.ReactNode;
}

export function DocumentUploadWithFrame({
  householdId,
  header,
  subheader,
  hideHeader = false,
  allowSelectExistingDocuments = true,
  excludedExistingDocumentTypes,
  errorMessage,
  onClearError,
  additionalContentAboveUploader,
}: DocumentUploadWithFrameProps) {
  const { uploadedFiles } = useMultiDocumentUploaderContext();

  return (
    <Stack spacing={2} minHeight="100%" justifyContent="center">
      {errorMessage && (
        <Alert severity="error" handleDismiss={onClearError}>
          {errorMessage}
        </Alert>
      )}
      {!hideHeader && (
        <Stack spacing={1}>
          <Typography variant="title-xl">{header}</Typography>
          <Typography variant="body-xs">{subheader}</Typography>
        </Stack>
      )}
      {additionalContentAboveUploader}
      <MultiDocumentUploaderWithListV2
        householdId={householdId}
        variant={uploadedFiles.length > 0 ? 'compact' : 'expanded'}
        allowSelectExistingDocuments={allowSelectExistingDocuments}
        excludedExistingDocumentTypes={excludedExistingDocumentTypes}
      />
    </Stack>
  );
}
